import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { useRefs, versOptions, listeOptions } from "@/lib/referentiels";
import { Champ } from "@/components/commun";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const AUCUN = { code: "_", label: "Aucun" };
const aucun = (v) => (v === "_" ? "" : v);
import { ChoixTags } from "./Tags";
import { ChampPerso } from "./ChampPerso";

export const ModifierDialog = ({ ouvert, setOuvert, fiche, champs, onEnregistre }) => {
  const { user } = useAuth();
  const refs = useRefs();
  const [f, setF] = useState({});
  const [c, setC] = useState({});
  const partenaire = user.role === "partenaire";
  const direction = ["dirigeant", "responsable"].includes(user.role);

  useEffect(() => {
    if (!ouvert) return;
    setF({ operation: fiche.operation, montant_estime: fiche.montant_estime, partenaire_id: fiche.partenaire_id || "", confirmateur_id: fiche.confirmateur_id || "",
      agent_id: fiche.agent_id || "", tags: fiche.tags.map((t) => t.id), champs: { ...fiche.champs }, ...(fiche.commission !== undefined ? { commission: fiche.commission } : {}) });
    const ct = fiche.contact || {};
    setC({ civilite: ct.civilite || "", prenom: ct.prenom || "", nom: ct.nom || "", telephones: (ct.telephones || []).join(", "), email: ct.email || "",
      adresse: ct.adresse || "", code_postal: ct.code_postal || "", ville: ct.ville || "", type: ct.type || "particulier", champs: { ...(ct.champs || {}) } });
  }, [ouvert, fiche]);

  const enregistrer = async (e) => {
    e.preventDefault();
    try {
      if (!partenaire) await api.patch(`/contacts/${fiche.contact_id}`, { ...c, telephones: c.telephones.split(",").map((t) => t.trim()).filter(Boolean) });
      const corps = { ...f };
      await api.patch(`/fiches/${fiche.id}`, corps);
      toast.success("Fiche enregistrée.");
      setOuvert(false); onEnregistre();
    } catch (err) { toast.error(messageErreur(err)); }
  };
  const mc = (k) => (e) => setC({ ...c, [k]: e?.target ? e.target.value : e });
  const equipe = (role) => (refs?.equipe || []).filter((u) => u.role === role).map((u) => ({ code: u.id, label: u.nom }));

  return (
    <Dialog open={ouvert} onOpenChange={setOuvert}>
      <DialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-fiche-dialog">
        <DialogHeader><DialogTitle>Modifier la fiche</DialogTitle></DialogHeader>
        <form onSubmit={enregistrer} className="space-y-5">
          {!partenaire && (
            <section className="space-y-3">
              <p className="font-bold">Contact</p>
              <div className="grid grid-cols-[90px_1fr] sm:grid-cols-[90px_1fr_1fr] gap-2 items-end">
                <ChoixListe label="Civilité" valeur={c.civilite || "_"} onChange={(v) => setC({ ...c, civilite: v === "_" ? "" : v })} options={[{ code: "_", label: "—" }, { code: "M.", label: "M." }, { code: "Mme", label: "Mme" }]} testId="edit-civilite" />
                <Champ label="Prénom" id="ed-prenom" dictee value={c.prenom || ""} onChange={mc("prenom")} data-testid="edit-prenom" />
                <div className="col-span-2 sm:col-span-1"><Champ label="Nom" id="ed-nom" dictee value={c.nom || ""} onChange={mc("nom")} data-testid="edit-nom" /></div>
              </div>
              <Champ label="Téléphones (séparés par des virgules)" id="ed-tel" value={c.telephones || ""} onChange={mc("telephones")} data-testid="edit-telephones" />
              <div className="grid sm:grid-cols-2 gap-2">
                <Champ label="E-mail" id="ed-mail" type="email" value={c.email || ""} onChange={mc("email")} data-testid="edit-email" />
                <ChoixListe label="Type" valeur={c.type} onChange={mc("type")} options={versOptions(refs?.types_contact)} testId="edit-type" />
              </div>
              <Champ label="Adresse" id="ed-adr" dictee value={c.adresse || ""} onChange={mc("adresse")} data-testid="edit-adresse" />
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <Champ label="Code postal" id="ed-cp" value={c.code_postal || ""} onChange={mc("code_postal")} data-testid="edit-cp" />
                <Champ label="Ville" id="ed-ville" dictee value={c.ville || ""} onChange={mc("ville")} data-testid="edit-ville" />
              </div>
              {champs.filter((d) => d.cible === "contact").map((d) => <ChampPerso key={d.id} def={d} valeur={c.champs?.[d.cle]} onChange={(v) => setC({ ...c, champs: { ...c.champs, [d.cle]: v } })} />)}
            </section>
          )}
          <section className="space-y-3">
            <p className="font-bold">Opportunité</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {!partenaire && <ChoixListe label="Opération" valeur={f.operation} onChange={(v) => setF({ ...f, operation: v })} options={listeOptions(refs?.operations)} testId="edit-operation" />}
              <Champ label="Montant estimé (€)" id="ed-montant" type="number" value={f.montant_estime ?? ""} onChange={(e) => setF({ ...f, montant_estime: e.target.value })} data-testid="edit-montant" />
              {f.commission !== undefined && <Champ label="Commission (€)" id="ed-com" type="number" value={f.commission ?? ""} onChange={(e) => setF({ ...f, commission: e.target.value })} data-testid="edit-commission" />}
              {!partenaire && <ChoixListe label="Partenaire" valeur={f.partenaire_id || "_"} onChange={(v) => setF({ ...f, partenaire_id: aucun(v) })} options={[AUCUN, ...(refs?.partenaires || []).map((p) => ({ code: p.id, label: p.nom }))]} testId="edit-partenaire" />}
              {!partenaire && user.role !== "confirmateur" && <ChoixListe label="Confirmateur" valeur={f.confirmateur_id || "_"} onChange={(v) => setF({ ...f, confirmateur_id: aucun(v) })} options={[AUCUN, ...equipe("confirmateur")]} testId="edit-confirmateur" />}
              {direction && <ChoixListe label="Agent" valeur={f.agent_id || "_"} onChange={(v) => setF({ ...f, agent_id: aucun(v) })} options={[AUCUN, ...equipe("agent")]} testId="edit-agent" />}
            </div>
            {!partenaire && <ChoixTags valeur={f.tags || []} onChange={(t) => setF({ ...f, tags: t })} />}
            {champs.filter((d) => d.cible === "fiche").map((d) => <ChampPerso key={d.id} def={d} valeur={f.champs?.[d.cle]} onChange={(v) => setF({ ...f, champs: { ...f.champs, [d.cle]: v } })} />)}
          </section>
          <button type="submit" className="at-btn at-btn-primary w-full" data-testid="edit-fiche-submit">Enregistrer</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
